/**
 * Значения по умолчанию для инструментов и проектов.
 *
 * Стиль стрелки по умолчанию взят из референса arrow.svg:
 *   stroke="#FF0000" stroke-width="7" stroke-linecap="round"
 */
import type { Project, Shape, ToolDefaults, ToolId } from './types';

export const DEFAULT_STROKE_COLOR = '#ff453a';
export const REFERENCE_RED = '#ff453a'; // стиль из arrow.svg
export const DEFAULT_STROKE_WIDTH = 7; // из arrow.svg
export const DEFAULT_FILL_COLOR = '#ff453a';
export const DEFAULT_TEXT_COLOR = '#ffffff';

export const BASE_DEFAULTS = {
  strokeColor: DEFAULT_STROKE_COLOR,
  strokeWidth: DEFAULT_STROKE_WIDTH,
  strokeEnabled: true,
  fillColor: DEFAULT_FILL_COLOR,
  fillEnabled: false,
  opacity: 1,
  fontSize: 24,
  fontFamily: 'Inter, system-ui, sans-serif',
  textColor: DEFAULT_TEXT_COLOR,
  radius: 22,
  headSize: 16,
  arrowLength: 120,
  hasArrow: true,
} satisfies ToolDefaults;

/** Настройки по умолчанию для каждого инструмента. */
export const TOOL_DEFAULTS: Record<ToolId, Partial<ToolDefaults>> = {
  select: {},
  arrow: {
    strokeColor: DEFAULT_STROKE_COLOR,
    strokeWidth: DEFAULT_STROKE_WIDTH,
    headSize: 16,
  },
  rect: {
    strokeColor: DEFAULT_STROKE_COLOR,
    strokeWidth: DEFAULT_STROKE_WIDTH,
    fillColor: DEFAULT_STROKE_COLOR,
    fillEnabled: false,
  },
  pencil: {
    strokeColor: REFERENCE_RED,
    strokeWidth: DEFAULT_STROKE_WIDTH,
    fillEnabled: false,
  },
  text: {
    textColor: '#111111',
    fontSize: 24,
    fontFamily: 'Inter, system-ui, sans-serif',
    fillColor: '#ffd23f',
    fillEnabled: true, // плашка под текстом по умолчанию
  },
  number: {
    strokeColor: DEFAULT_STROKE_COLOR,
    strokeWidth: 4,
    strokeEnabled: false,
    fillColor: DEFAULT_STROKE_COLOR,
    fillEnabled: true,
    textColor: DEFAULT_TEXT_COLOR,
    radius: 22,
    arrowLength: 120,
    hasArrow: true,
  },
  blur: {
    fillColor: '#000000',
    fillEnabled: true,
  },
};

let counter = 0;
export function uid(prefix = 's'): string {
  counter += 1;
  return `${prefix}_${Date.now().toString(36)}_${counter.toString(36)}`;
}

export function now(): number {
  return Date.now();
}

export function createProject(name = 'Без названия'): Project {
  return {
    id: uid('p'),
    name,
    image: null,
    shapes: [],
    viewport: { scale: 1, tx: 0, ty: 0 },
    background: {
      enabled: false,
      color: '#ffffff',
      padding: 50,
      cornerRadius: 12,
      shadowEnabled: true,
    },
    createdAt: now(),
    updatedAt: now(),
  };
}

/** Объединить настройки инструмента с базовыми дефолтами. */
export function resolveToolDefaults(
  tool: ToolId,
  overrides: Partial<ToolDefaults> | undefined,
): ToolDefaults {
  return { ...BASE_DEFAULTS, ...TOOL_DEFAULTS[tool], ...overrides };
}

/** Создать новый shape заданного типа с дефолтами инструмента. */
export function createShape(
  tool: ToolId,
  patch: Partial<Shape>,
  z: number,
  toolSettings: Partial<ToolDefaults>,
): Shape {
  const d = resolveToolDefaults(tool, toolSettings);
  const base = {
    id: uid('shape'),
    x: 0,
    y: 0,
    rotation: 0,
    opacity: d.opacity,
    strokeColor: d.strokeColor,
    strokeWidth: d.strokeWidth,
    strokeEnabled: d.strokeEnabled,
    fillColor: d.fillColor,
    fillEnabled: d.fillEnabled,
    visible: true,
    locked: false,
    z,
    name: '',
    createdAt: now(),
  };

  switch (tool) {
    case 'arrow':
      return {
        ...base,
        type: 'arrow',
        x2: 0,
        y2: 0,
        headStyle: 'arrow',
        headSize: d.headSize ?? 16,
        dash: 'solid',
        curved: false,
        path: undefined,
        marker: undefined,
        ...patch,
      } as Shape;
    case 'rect':
      return {
        ...base,
        type: 'rect',
        w: 0,
        h: 0,
        cornerRadius: 6,
        marker: undefined,
        fillMode: 'color',
        intensity: 12,
        ...patch,
      } as Shape;
    case 'pencil':
      return {
        ...base,
        type: 'pencil',
        points: [],
        closed: false,
        smoothing: 0.5,
        ...patch,
      } as Shape;
    case 'text':
      return {
        ...base,
        type: 'text',
        text: '',
        fontFamily: d.fontFamily,
        fontSize: d.fontSize,
        bold: false,
        italic: false,
        underline: false,
        strikethrough: false,
        align: 'left',
        hasBackground: d.fillEnabled,
        textColor: d.textColor,
        ...patch,
      } as Shape;
    case 'number':
      return {
        ...base,
        strokeWidth: d.strokeWidth,
        strokeEnabled: d.strokeEnabled,
        fillEnabled: d.fillEnabled,
        type: 'number',
        value: 1,
        radius: d.radius,
        hasArrow: true,
        arrowAngle: 0,
        arrowLength: 120,
        arrowMode: 'target',
        arrowTarget: { x: 0, y: 0 },
        textColor: d.textColor,
        headStyle: 'arrow',
        headSize: 18,
        ...patch,
      } as Shape;
    case 'blur':
      return {
        ...base,
        type: 'blur',
        w: 0,
        h: 0,
        mode: 'pixelize',
        intensity: 12,
        ...patch,
      } as Shape;
    default:
      throw new Error(`Unknown tool: ${tool}`);
  }
}
